import { get, post, put, del } from '../../utils/ApiUtil';
import Event from '../../models/Event';
import Page from '../../filters/Page';

/**
 * Convert Laravel pagination response to Page class
 * @param {Object} paginationData
 * @returns {Page}
 */
function toPage(paginationData) {
  const pageData = {
    data: paginationData.data,
    meta: {
      current_page: paginationData.current_page,
      from: paginationData.from,
      to: paginationData.to,
      last_page: paginationData.last_page,
      per_page: paginationData.per_page,
      total: paginationData.total
    }
  };
  return new Page(pageData, Event);
}

/**
 * Get paginated events (for /events page)
 *
 * @param filter
 * @returns {Promise<Page>}
 */
export async function getPaginatedEvents(filter) {
  const response = await get('/api/user/events', filter);
  return toPage(response.data);
}

/**
 * Get paginated master events (for /master/events page)
 *
 * @param filter
 * @returns {Promise<Page>}
 */
export async function getPaginatedMasterEvents(filter) {
  const response = await get('/api/user/master/events', filter);
  return toPage(response.data);
}

/**
 * Get single master event with organizers and fees
 *
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getMasterEvent(id) {
  const response = await get(`/api/user/master/events/${id}`);
  return response.data;
}

/**
 * Create master event with organizers and fees
 *
 * @param {Object} eventData
 * @returns {Promise<Object>}
 */
export async function createMasterEvent(eventData) {
  const response = await post('/api/user/master/events', eventData);
  return response.data;
}

/**
 * Update master event with organizers and fees
 *
 * @param {string} id
 * @param {Object} eventData
 * @returns {Promise<Object>}
 */
export async function updateMasterEvent(id, eventData) {
  const response = await put(`/api/user/master/events/${id}`, eventData);
  return response.data;
}

/**
 * Delete master event
 *
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function deleteMasterEvent(id) {
  await del(`/api/user/master/events/${id}`);
}

/**
 * Check if master event can be deleted
 *
 * @param {string} id
 * @returns {Promise<{deletable: boolean, message?: string}>}
 */
export async function checkMasterEventDeletable(id) {
  const response = await get(`/api/user/master/events/${id}/check-deletable`);
  return response;
}

/**
 * Get all event organizers for dropdown selection
 *
 * @param {string} search - Optional search query
 * @returns {Promise<Array>}
 */
export async function getAllEventOrganizers(search = '') {
  const params = { limit: 1000 };
  if (search) {
    params.name = search;
  }
  const response = await get('/api/user/master/event_organizers', params);
  return response.data.data;
}

/**
 * Create multiple master events (bulk creation)
 *
 * @param {Object} data - { events: Array, images: Array }
 * @returns {Promise<Array>}
 */
export async function createMasterEventsBulk(data) {
  const response = await post('/api/user/master/events/bulk', data);
  return response.data;
}

/**
 * Analyze multiple event flyer images using Gemini AI
 *
 * @param {FileList|Array<File>} images - Array of image files to analyze
 * @returns {Promise<{events: Array, images: Array}>}
 */
export async function analyzeEventFlyersMultiple(images) {
  const formData = new FormData();

  for (let i = 0; i < images.length; i++) {
    formData.append('images[]', images[i]);
  }

  const response = await post('/api/user/event-flyer-analysis/analyze-multiple', formData, {
    'Content-Type': 'multipart/form-data'
  });
  return response.data;
}
